// The Columns tab: the column dictionary of each tabular file of a dataset (plan feature 4).
//
// Props (see DatasetPage.jsx): { dataset, files, canEdit, reloadDataset, reloadFiles }
//
//   GET   /api/files/{id}/columns                   the dictionary of the selected file
//   PATCH /api/columns/{id}                         ColumnEditor ("Save and confirm")
//   PATCH /api/files/{id}/columns                   ColumnGrid: every changed row in one request
//   POST  /api/files/{id}/reinfer                   re-read the file, keep descriptions and rules
//   GET   /api/files/{id}/profile                   FileProfile: preview, stats, quality, issues
//   GET   /api/datasets/{id}/datapackage.json       Frictionless export of the whole dataset
//   PUT   /api/files/{id}/file-patterns             FilePatterns: file names built from columns
//
// Helpers live in ./columns/ (ColumnEditor, ColumnGrid + grid.js, FileProfile, ColumnStats, terms).
// "Entries" (one column at a time) and "Grid" (spreadsheet-like, paste from TSV) are two views
// of the same dictionary; only the dataset's editors (can_edit) get the grid.
import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { Empty, ErrorNote, Loading, SectionTitle } from "../../components/Feedback";
import { Badge, Btn, Card, G, G_BG, G_DARK, NeedsLargerScreen } from "../../components/Shared";
import { focusWhenReady } from "../../focus";
import { useApi } from "../../useApi";
import ColumnEditor from "./columns/ColumnEditor";
import ColumnGrid from "./columns/ColumnGrid";
import FilePatterns from "./columns/FilePatterns";
import FileProfile from "./columns/FileProfile";
import { MARKED, ruleBadges } from "./columns/terms";

const th = { padding: "10px 12px", textAlign: "left", fontSize: 12, color: "#6b7280", textTransform: "uppercase",
             letterSpacing: "0.05em", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", verticalAlign: "top", borderTop: "1px solid #f3f4f6" };
const muted = { color: "#9ca3af" };

function FilePicker({ files, selectedId, onSelect }) {
  if (files.length < 2) return null;
  return (
    <div role="group" aria-label="Table" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
      {files.map(f => {
        const on = f.id === selectedId;
        return (
          <button key={f.id} type="button" aria-pressed={on} onClick={() => onSelect(f.id)}
                  style={{ padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                           fontFamily: "inherit", fontWeight: on ? 700 : 500, border: "1px solid",
                           background: on ? G_BG : "#ffffff", color: on ? G_DARK : "#374151",
                           borderColor: on ? G : "#d1d5db" }}>
            {f.filename} <span style={{ color: on ? G_DARK : "#4b5563" }}>({f.column_count})</span>
          </button>
        );
      })}
    </div>
  );
}

function Dictionary({ dataset, file, canEdit, reloadFiles }) {
  const cols = useApi(`/files/${file.id}/columns`);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("list");
  const [confirmReinfer, setConfirmReinfer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const list = Array.isArray(cols.data) ? cols.data : [];
  const confirmed = list.filter(c => c.confirmed).length;
  // The editor (or the re-infer question) unmounts on save/cancel: give focus back to the
  // button that opened it instead of letting it fall to <body>.
  const backTo = key => focusWhenReady(() => document.querySelector(`[data-focus-return="${key}"] button`));
  const closeEditor = id => { setEditing(null); backTo(`edit-${id}`); };
  const editorId = `column-editor-${file.id}`;
  const editingCol = list.find(c => c.id === editing) || null;
  // Opening an entry moves focus to its heading below the table, so a keyboard user lands on it
  // (the Edit button keeps focus otherwise, so focusWhenReady, which only fills a lost focus, is
  // not enough here).
  const focusEditor = useRef(false);
  const openEditor = id => { focusEditor.current = true; setEditing(id); };
  useEffect(() => {
    if (!focusEditor.current || editing == null) return;
    focusEditor.current = false;
    document.getElementById(`${editorId}-h`)?.focus();
  }, [editing, editorId]);
  const closeReinfer = () => { setConfirmReinfer(false); backTo(`reinfer-${file.id}`); };

  const reinfer = async () => {
    setBusy(true); setError(null); setNotice("");
    try {
      const out = await api.post(`/files/${file.id}/reinfer`);
      setNotice(`Re-read ${file.filename}: ${out.columns.length} columns.`);
      closeReinfer(); setEditing(null);
      cols.reload(); reloadFiles();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const gridSaved = (_cols, n) => {
    setNotice(`Saved ${n} column${n === 1 ? "" : "s"} in one request.`);
    cols.reload();
  };

  const saved = col => {
    closeEditor(col.id);
    setNotice(`Saved the entry for ${col.name}.`);
    cols.reload();
  };

  return (
    <Card style={{ padding: "20px 22px" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>{file.filename}</h3>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span>{file.row_count ?? "?"} rows · {list.length || file.column_count} columns</span>
            {list.length > 0 && (
              <Badge color={confirmed === list.length ? "green" : "amber"}>{confirmed}/{list.length} confirmed</Badge>
            )}
          </div>
        </div>
        {canEdit && list.length > 0 && (
          <div role="group" aria-label="View" style={{ display: "inline-flex", gap: 0 }}>
            {[["list", "Entries"], ["grid", "Grid"]].map(([v, label], i) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => { setView(v); setEditing(null); }}
                      style={{ padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                               fontWeight: view === v ? 700 : 500, border: "1px solid",
                               borderColor: view === v ? G : "#d1d5db", background: view === v ? G_BG : "#ffffff",
                               color: view === v ? G_DARK : "#374151",
                               borderRadius: i === 0 ? "999px 0 0 999px" : "0 999px 999px 0" }}>
                {label}
              </button>
            ))}
          </div>
        )}
        {canEdit && !confirmReinfer && (
          <span data-focus-return={`reinfer-${file.id}`}>
            <Btn variant="secondary" onClick={() => setConfirmReinfer(true)}>Re-infer types</Btn>
          </span>
        )}
      </div>
      {confirmReinfer && (
        <div role="group" aria-label="Confirm re-inference"
             style={{ padding: "12px 14px", borderRadius: 10, background: G_BG, border: "1px solid rgba(0,150,64,0.3)",
                      marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, flex: 1, minWidth: 220 }}>
            Read {file.filename} again and re-infer types and constraints? Titles, descriptions,
            missing-value markers, key links and shared-entity memberships of columns with the same name are
            kept; every entry needs confirming again.
          </span>
          <Btn onClick={reinfer} disabled={busy}>{busy ? "Reading…" : "Re-infer"}</Btn>
          <Btn variant="secondary" onClick={closeReinfer} disabled={busy}>Cancel</Btn>
        </div>
      )}
      <div role="status" aria-live="polite" style={{ fontSize: 13, color: G_DARK, minHeight: notice ? 18 : 0, marginBottom: notice ? 8 : 0 }}>
        {notice}
      </div>
      <ErrorNote error={error} />
      {file.inference_error && (
        <p style={{ fontSize: 13, color: "#92400e", margin: "0 0 12px" }}>{file.inference_error}</p>
      )}
      <ErrorNote error={cols.error} />
      {cols.loading && !cols.data && <Loading label="Loading columns…" />}
      {cols.data && list.length === 0 && <Empty>This file has no columns yet.</Empty>}
      {list.length > 0 && canEdit && view === "grid" && (
        // A spreadsheet-like editor: below 768 px it says so first ("Show it anyway").
        <NeedsLargerScreen what="The column grid">
          <ColumnGrid file={file} columns={list} onSaved={gridSaved} onClose={() => setView("list")} />
        </NeedsLargerScreen>
      )}
      {list.length > 0 && !(canEdit && view === "grid") && (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
          <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <caption style={{ position: "absolute", left: -9999 }}>Column dictionary of {file.filename}</caption>
            <thead>
              <tr>
                <th scope="col" style={th}>Column</th>
                <th scope="col" style={th}>Type</th>
                <th scope="col" style={th}>Description</th>
                <th scope="col" style={th}>Status</th>
                <th scope="col" style={th}><span style={{ position: "absolute", left: -9999 }}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {list.map(c => {
                const cons = c.constraints || {};
                const open = editing === c.id;
                return (
                  <tr key={c.id} style={{ background: open ? "#f9fafb" : undefined }}>
                    <th scope="row" style={{ ...td, textAlign: "left", fontWeight: 600 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{c.name}</span>
                      {c.title && <span style={{ display: "block", fontWeight: 400, fontSize: 12, color: "#374151" }}>{c.title}</span>}
                      {c.example_values.length > 0 && (
                        <span style={{ display: "block", fontWeight: 400, fontSize: 11, color: "#6b7280", maxWidth: 220,
                                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={c.example_values.join(", ")}>
                          e.g. {c.example_values.slice(0, 3).join(", ")}
                        </span>
                      )}
                    </th>
                    <td data-label="Type" style={td}>
                      <Badge color="blue">{c.type}</Badge>
                      <span style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                        {cons.required && <Badge color="gray">required</Badge>}
                        {cons.unique && <Badge color="gray">unique</Badge>}
                        {ruleBadges(cons).map(b => <Badge key={b} color="gray">{b}</Badge>)}
                        {c.logical_type === "file" && <Badge color="green">file{c.file_role ? `: ${c.file_role}` : ""}</Badge>}
                        {MARKED.includes(c.sensitivity) && <Badge color="red">{c.sensitivity} data</Badge>}
                        {c.sensitivity === "checked" && <Badge color="gray">checked: not personal</Badge>}
                      </span>
                    </td>
                    <td data-label="Description" style={{ ...td, maxWidth: 320 }}>{c.description || <span style={muted}>—</span>}</td>
                    <td data-label="Status" style={td}>
                      {c.confirmed ? <Badge color="green">✓ confirmed</Badge> : <Badge color="amber">inferred</Badge>}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {canEdit && (
                        <span data-focus-return={`edit-${c.id}`}>
                          <Btn variant={open ? "secondary" : "ghost"} onClick={() => (open ? setEditing(null) : openEditor(c.id))}
                               style={{ padding: "6px 14px" }}>
                            <span aria-hidden="true">{open ? "Close" : "Edit"}</span>
                            <span style={{ position: "absolute", left: -9999 }}>{open ? `Close ${c.name}` : `Edit ${c.name}`}</span>
                          </Btn>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* The entry being edited opens below the table, not in a row of it: inside the table it
          was as wide as the seven columns, and at 200 % zoom or with 150 % text its left half
          sat outside the frame's sideways scroll (a11y pass 2026-10-04). Here it has the card's
          width at any zoom. */}
      {editingCol && (
        <section id={editorId} aria-labelledby={`${editorId}-h`}
                 style={{ marginTop: 14, padding: "16px 18px", borderRadius: 12, background: "#f9fafb",
                          border: "1px solid #e5e7eb", minWidth: 0 }}>
          <h4 id={`${editorId}-h`} tabIndex={-1} style={{ margin: "0 0 12px", fontSize: 15, outline: "none" }}>
            Editing <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, overflowWrap: "anywhere" }}>{editingCol.name}</span>
          </h4>
          <ColumnEditor key={editingCol.id} column={editingCol} dataset={dataset} onSaved={saved} onCancel={() => closeEditor(editingCol.id)} />
        </section>
      )}
      {list.length > 0 && <FilePatterns dataset={dataset} file={file} columns={list} canEdit={canEdit} reloadFiles={reloadFiles} />}
      {!file.inference_error && <FileProfile key={file.id} file={file} />}
    </Card>
  );
}

export default function ColumnsTab({ dataset, files, canEdit, reloadFiles }) {
  const tabular = files.filter(f => f.is_tabular);
  const [selectedId, setSelectedId] = useState(tabular.length ? tabular[0].id : null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tabular.some(f => f.id === selectedId)) setSelectedId(tabular.length ? tabular[0].id : null);
  }, [tabular, selectedId]);

  const selected = tabular.find(f => f.id === selectedId) || tabular[0];
  const download = () => {
    setError(null);
    api.download(`/datasets/${dataset.id}/datapackage.json`, "datapackage.json").catch(setError);
  };

  return (
    <div>
      <SectionTitle right={files.length > 0 && <Btn variant="secondary" onClick={download}>Download datapackage.json</Btn>}>
        Column dictionary
      </SectionTitle>
      <p style={{ fontSize: 14, color: "#374151", margin: "0 0 16px", maxWidth: 760 }}>
        Each column of a table gets a type, a title and a description (with its unit, e.g. “Sampling depth (m)”).
        Types are inferred on upload; confirm or correct them so the project database is built right.
      </p>
      <ErrorNote error={error} />
      {tabular.length === 0 ? (
        <Empty>No tabular files yet. Upload a CSV, TSV or XLSX file to get a column dictionary.</Empty>
      ) : (
        <>
          <FilePicker files={tabular} selectedId={selected.id} onSelect={setSelectedId} />
          <Dictionary key={selected.id} dataset={dataset} file={selected} canEdit={canEdit} reloadFiles={reloadFiles} />
        </>
      )}
    </div>
  );
}
